import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ChatBubbleOutlineOutlined as ChatBubbleOutlineOutlinedIcon,
  ModeOfTravelOutlined as ModeOfTravelOutlinedIcon,
  ArrowForwardIosOutlined as ArrowForwardIosOutlinedIcon,
  CheckCircleOutlineRounded as CheckCircleOutlineRoundedIcon,
} from '@mui/icons-material';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useFeatureReviewInvoicing } from '../../../hooks/features/useFeatureReviewInvoicing';

import { TripLegSearchMenu } from '../../../data/requests/trip-legs';
import { TripLegEventType } from '../../../services/webSocketService/updateTripLeg';

import * as triplegSlice from '../../../data/tripleg.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';

import dateUtils from '../../../services/DateUtils';

const PREFIX = 'PR2HomePage';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  section: `${PREFIX}-section`,
  icon: `${PREFIX}-icon`,
  info: `${PREFIX}-info`,
  nav: `${PREFIX}-nav`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '752px',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(4),
    paddingBottom: 0,
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    paddingTop: theme.spacing(3),
  },
  [`& .${classes.section}`]: {
    backgroundColor: '#0110120A',
    padding: theme.spacing(3),
    paddingRight: theme.spacing(2),
    borderRadius: '8px',
    alignItems: 'center',
  },
  [`& .${classes.icon}`]: {
    color: '#0110128A',
  },
  [`& .${classes.info}`]: {
    marginLeft: theme.spacing(3),
  },
  [`& .${classes.nav}`]: {
    marginLeft: 'auto',
    backgroundColor: '#0110120A',

    [`&:hover`]: {
      backgroundColor: '#01101214',
    },
  },
}));

interface PR2HomeWidgetProps {
  title: string;
  icon: React.ElementType;
  tripCount?: number;
  dueFieldLabel?: string;
  dueFieldValue?: string;
  navigationAction: () => void;
  isLoading?: boolean;
}

const PR2HomeWidget = ({
  title,
  icon: Icon,
  tripCount = 0,
  dueFieldLabel,
  dueFieldValue,
  navigationAction = () => {},
  isLoading,
}: PR2HomeWidgetProps) => {
  const { t } = useTranslation('common');

  return (
    <Grid container className={classes.section}>
      {isLoading && <CircularProgress color="primary" />}
      {!isLoading && (
        <>
          <Grid item xs={4}>
            <Stack direction="row" spacing={1}>
              <Icon className={classes.icon} fontSize="large" />
              <Typography variant="h3">{title}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={2}>
            <Box component="div" className={classes.info}>
              <Typography variant="overline">{t('home.trips')}</Typography>
              <Typography variant="h3">{tripCount}</Typography>
            </Box>
          </Grid>

          <Grid item xs={5}>
            <Box component="div" className={classes.info}>
              {dueFieldValue && (
                <Grid item>
                  <Typography variant="overline">
                    {dueFieldLabel || t('home.due')}
                  </Typography>
                  <Typography variant="h3">{dueFieldValue}</Typography>
                </Grid>
              )}
            </Box>
          </Grid>

          <Grid item xs={1}>
            <IconButton
              className={classes.nav}
              onClick={navigationAction}
              size="large">
              <ArrowForwardIosOutlinedIcon fontSize="small" />
            </IconButton>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default function PR2HomePage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const enableReviewInvoicing = useFeatureReviewInvoicing();

  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const todaysLegs = useAppSelector(triplegSlice.selectTodaysTripLegs);
  const loadingTodaysTrips = useAppSelector(
    triplegSlice.selectLoadingTodaysTripLegs,
  );
  const discussionLegs = useAppSelector(triplegSlice.selectDiscussionTripsLegs);
  const loadingDiscussionTrips = useAppSelector(
    triplegSlice.selectLoadingDiscussionTripLegs,
  );
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );
  const needsReviewData = useAppSelector(accountingSlice.selectNeedsReviewData);
  const loadingNeedsReview = useAppSelector(
    accountingSlice.selectLoadingNeedsReview,
  );

  const fetchTripLegs = () => {
    const today = DateTime.now();
    dispatch(
      triplegSlice.fetchTodaysTripLegsAsync({
        menu: TripLegSearchMenu.ESCALATED,
        todayDate: today.toFormat('yyyy-MM-dd'),
        page: 1,
        pageSize: 1,
        sort: 'pickUpDueTimeUTC',
        sortOrder: 'asc',
      }),
    );
    dispatch(
      triplegSlice.fetchDiscussionTripLegsAsync({
        page: 1,
        pageSize: 1,
        sort: 'appointmentDate',
        sortOrder: 'asc',
      }),
    );
  };

  useEffect(() => {
    fetchTripLegs();
    if (enableReviewInvoicing) {
      dispatch(accountingSlice.fetchNeedsReviewAsync());
    }
  }, []);

  useEffect(() => {
    if (
      tripLegEventEmitted &&
      (tripLegEventEmitted.detailType === TripLegEventType.TripLegOpened ||
        tripLegEventEmitted.detailType === TripLegEventType.TripLegEscalated)
    ) {
      fetchTripLegs();
      if (
        tripLegEventEmitted.detailType === TripLegEventType.TripLegEscalated
      ) {
        dispatch(triplegSlice.fetchTripLegCountsAsync());
      }
      dispatch(triplegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEventEmitted]);

  const discussTrips = () => {
    navigate('/discussions');
  };

  const escalatedTrips = () => {
    navigate('/today');
  };

  const invoiceTrips = () => {
    navigate('/review-invoices');
  };

  const getTodaysDueTime = useMemo(() => {
    const tripLeg = todaysLegs?.items.length ? todaysLegs.items[0] : null;
    if (tripLeg) {
      const pickupDate = tripLeg.pickUpDueTimeUTC;
      const tz = timezones.find(
        (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
      )?.ianaStandard;
      if (pickupDate && tz) {
        return dateUtils.formatAppointmentTime(pickupDate, tz);
      }
    }
    return '';
  }, [todaysLegs, timezones]);

  const getDiscussionDueDate = useMemo(() => {
    const tripLeg = discussionLegs?.items.length
      ? discussionLegs.items[0]
      : null;
    if (tripLeg) {
      const apptDate = tripLeg.appointmentDate;
      if (apptDate) {
        return dateUtils.formatDate(
          DateTime.fromISO(apptDate).plus({ days: 7 }),
        );
      }
    }
    return '';
  }, [discussionLegs]);

  const getInvoiceDueDate = useMemo(() => {
    if (!needsReviewData) return '';

    let allLineItems: any[] = [];

    if (needsReviewData.groupedByProvider && needsReviewData.providers) {
      needsReviewData.providers.forEach((providerGroup) => {
        allLineItems.push(...providerGroup.lineItems);
      });
    } else if (needsReviewData.lineItems) {
      allLineItems = needsReviewData.lineItems;
    }

    if (allLineItems.length === 0) return '';

    const earliestDate = allLineItems.reduce(
      (earliest, item) => {
        const itemDate = DateTime.fromISO(item.createdOnUTC);
        return !earliest || itemDate < earliest ? itemDate : earliest;
      },
      null as DateTime | null,
    );

    return earliestDate ? dateUtils.formatDate(earliestDate) : '';
  }, [needsReviewData]);

  return (
    <StyledPaper className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('home.tasks')}
        </Typography>
      </Box>

      <Box className={classes.content}>
        <PR2HomeWidget
          title={t('home.escalatedTrips')}
          icon={ModeOfTravelOutlinedIcon}
          tripCount={todaysLegs?.itemCount}
          dueFieldLabel={t('home.nextPickup')}
          dueFieldValue={getTodaysDueTime}
          navigationAction={escalatedTrips}
          isLoading={loadingTodaysTrips}
        />

        <PR2HomeWidget
          title={t('home.discussTrips')}
          icon={ChatBubbleOutlineOutlinedIcon}
          tripCount={discussionLegs?.itemCount}
          dueFieldValue={getDiscussionDueDate}
          navigationAction={discussTrips}
          isLoading={loadingDiscussionTrips}
        />

        {enableReviewInvoicing &&
          !loadingNeedsReview &&
          (needsReviewData?.totalLineItems ?? 0) > 0 && (
            <PR2HomeWidget
              title={t('home.reviewInvoicing')}
              icon={CheckCircleOutlineRoundedIcon}
              tripCount={needsReviewData?.totalLineItems}
              dueFieldValue={getInvoiceDueDate}
              navigationAction={invoiceTrips}
            />
          )}
      </Box>
    </StyledPaper>
  );
}
